import React, { useState, useEffect, useRef, useCallback } from 'react';

const COLORS = {
  border: '#CBD2D9',
  alertRed: '#BA1405',
  pillBorder: '#F6D9D7',
  pillFill: '#FEEDEB',
  titleText: '#1F2933',
  pillText: '#3E4C59',
};

const ANIM_DURATION = 1500;

// Easing utilities
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// Smooth 0→1→0 hump for pulses / glow flashes
function hump(t: number): number {
  if (t <= 0 || t >= 1) return 0;
  return Math.sin(t * Math.PI);
}

// Windowed spring arrival (same principle as SubscriptionsChart)
// Guaranteed value=1 and slope=0 at t=1
function springArrive(t: number, overshoot: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const base = 1 - Math.pow(1 - t, 4);
  const windowFade = (1 - t) * (1 - t);
  const bounce = windowFade * Math.sin(t * Math.PI * 2) * overshoot;
  return base + bounce;
}

// ========== Icons ==========

const WarningIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3L2 21H22L12 3Z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
      fill="none"
    />
    <path d="M12 10V14" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17.5" r="1" fill={color} />
  </svg>
);

// Icons are traced verbatim from the reference SVG paths.
// Using viewBox cropping so original coordinates work unchanged.
const ICON_COLOR = '#3E4C59';

const SsnIcon: React.FC = () => (
  <svg width="26" height="24" viewBox="93 87 28 25" fill="none">
    <path
      d="M104.665 93H97.9987C96.5259 93 95.332 94.1939 95.332 95.6667V107.667C95.332 109.139 96.5259 110.333 97.9987 110.333H116.665C118.138 110.333 119.332 109.139 119.332 107.667V95.6667C119.332 94.1939 118.138 93 116.665 93H109.999M104.665 93V91.6667C104.665 90.1939 105.859 89 107.332 89C108.805 89 109.999 90.1939 109.999 91.6667V93M104.665 93C104.665 94.4728 105.859 95.6667 107.332 95.6667C108.805 95.6667 109.999 94.4728 109.999 93M103.332 103.667C104.805 103.667 105.999 102.473 105.999 101C105.999 99.5272 104.805 98.3333 103.332 98.3333C101.859 98.3333 100.665 99.5272 100.665 101C100.665 102.473 101.859 103.667 103.332 103.667ZM103.332 103.667C105.074 103.667 106.555 104.78 107.104 106.333M103.332 103.667C101.59 103.667 100.109 104.78 99.5596 106.333M111.332 99.6667H115.332M111.332 105H113.999"
      stroke={ICON_COLOR}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CreditCardIcon: React.FC = () => (
  <svg width="22" height="18" viewBox="530 69 22 18" fill="none">
    <path
      d="M532 76H550M536 81H537M541 81H542M535 85H547C548.657 85 550 83.6569 550 82V74C550 72.3431 548.657 71 547 71H535C533.343 71 532 72.3431 532 74V82C532 83.6569 533.343 85 535 85Z"
      stroke={ICON_COLOR}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PasswordIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="538 113 22 22" fill="none">
    <path
      d="M552 119C553.105 119 554 119.895 554 121M558 121C558 124.314 555.314 127 552 127C551.394 127 550.809 126.91 550.257 126.743L548 129H546V131H544V133H541C540.448 133 540 132.552 540 132V129.414C540 129.149 540.105 128.895 540.293 128.707L546.257 122.743C546.09 122.191 546 121.606 546 121C546 117.686 548.686 115 552 115C555.314 115 558 117.686 558 121Z"
      stroke={ICON_COLOR}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CarIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="900 68 22 22" fill="none">
    {/* Car body top */}
    <path
      d="M902.914 77.75C902.914 77.2196 903.125 76.7109 903.5 76.3358C903.875 75.9607 904.384 75.75 904.914 75.75H917.414C917.944 75.75 918.453 75.9607 918.828 76.3358C919.203 76.7109 919.414 77.2196 919.414 77.75V83.75H902.914V77.75Z"
      stroke={ICON_COLOR}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Wheel accents (tiny dots) */}
    <path
      d="M906.164 78.5C906.44 78.5 906.664 78.7239 906.664 79C906.664 79.2761 906.44 79.5 906.164 79.5C905.888 79.5 905.664 79.2761 905.664 79C905.664 78.7239 905.888 78.5 906.164 78.5Z"
      stroke={ICON_COLOR}
      fill={ICON_COLOR}
    />
    <path
      d="M916.164 78.5C916.44 78.5 916.664 78.7239 916.664 79C916.664 79.2761 916.44 79.5 916.164 79.5C915.888 79.5 915.664 79.2761 915.664 79C915.664 78.7239 915.888 78.5 916.164 78.5Z"
      stroke={ICON_COLOR}
      fill={ICON_COLOR}
    />
    {/* Roof, windshield arc, and wheel wells */}
    <path
      d="M909.164 80.75H913.164M905.435 71.567C905.575 71.181 905.831 70.8476 906.167 70.6119C906.504 70.3763 906.904 70.25 907.315 70.25H915.013C915.424 70.25 915.824 70.3763 916.161 70.6119C916.497 70.8476 916.753 71.181 916.893 71.567L918.414 75.75H903.914L905.435 71.567ZM902.914 83.75H906.352V85.45C906.352 85.7948 906.215 86.1254 905.971 86.3692C905.728 86.613 905.397 86.75 905.052 86.75H904.214C903.869 86.75 903.539 86.613 903.295 86.3692C903.051 86.1254 902.914 85.7948 902.914 85.45V83.75ZM915.977 83.75H919.414V85.45C919.414 85.7948 919.277 86.1254 919.033 86.3692C918.79 86.613 918.459 86.75 918.114 86.75H917.277C916.932 86.75 916.602 86.613 916.358 86.3692C916.114 86.1254 915.977 85.7948 915.977 85.45V83.75Z"
      stroke={ICON_COLOR}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CakeIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="909 113 22 22" fill="none">
    <path
      d="M929.164 127.546C928.641 127.546 928.118 127.697 927.664 128C926.756 128.606 925.572 128.606 924.664 128C923.756 127.394 922.572 127.394 921.664 128C920.756 128.606 919.572 128.606 918.664 128C917.756 127.394 916.572 127.394 915.664 128C914.756 128.606 913.572 128.606 912.664 128C912.21 127.697 911.687 127.546 911.164 127.546M917.164 118V120M920.164 118V120M923.164 118V120M917.164 115H917.174M920.164 115H920.174M923.164 115H923.174M929.164 133V126C929.164 124.895 928.269 124 927.164 124H913.164C912.059 124 911.164 124.895 911.164 126V133H929.164ZM926.164 124V122C926.164 120.895 925.269 120 924.164 120H916.164C915.059 120 914.164 120.895 914.164 122V124H926.164Z"
      stroke={ICON_COLOR}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ========== Data ==========

interface Breach {
  name: string;
  Icon: React.FC;
  span: boolean;
  // Tooltip copy
  tooltipTitle: string;
  // Noun used inside the sentence: "Your <subject> was exposed by N different breaches"
  tooltipSubject: string;
  count: number;
}

const BREACHES: Breach[] = [
  { name: 'Social security number', Icon: SsnIcon,         span: true,  tooltipTitle: 'Social Security Number', tooltipSubject: 'Social Security Number', count: 2 },
  { name: 'Credit Card',            Icon: CreditCardIcon,  span: false, tooltipTitle: 'Credit Card',            tooltipSubject: 'credit card info',        count: 3 },
  { name: "Driver's license",       Icon: CarIcon,         span: false, tooltipTitle: "Driver's License",       tooltipSubject: "driver's license",        count: 1 },
  { name: 'Password',               Icon: PasswordIcon,    span: false, tooltipTitle: 'Password',               tooltipSubject: 'password',                count: 4 },
  { name: 'Date of birth',          Icon: CakeIcon,        span: false, tooltipTitle: 'Date of Birth',          tooltipSubject: 'date of birth',           count: 2 },
];

// Per-pill animation traits
// Order: SSN appears first (biggest threat), then right column top-to-bottom
// appearanceDelay: when pill starts fading in (as fraction of total progress)
// Pills are laid out in grid order (DOM order matters for CSS grid placement):
// SSN (span 2) | Credit Card | Driver's License
//              | Password    | Date of birth
const PILL_DELAYS: number[] = [
  0.10, // SSN — most critical, appears first
  0.24, // Credit Card
  0.34, // Driver's license
  0.44, // Password
  0.54, // Date of birth
];

// ========== Component ==========

export const BreachesChart: React.FC = () => {
  const [rawProgress, setRawProgress] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const startTimeRef = useRef<number>(0);
  const rafRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAnimating = rawProgress < 1;

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;
    const t = Math.min(1, elapsed / ANIM_DURATION);
    setRawProgress(t);
    if (t < 1) {
      rafRef.current = requestAnimationFrame(animate);
    }
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate]);

  // Warning triangle: single alert pulse during t=[0, 0.25]
  const iconPulseT = Math.min(1, rawProgress / 0.25);
  const iconScale = 1 + 0.25 * hump(iconPulseT);
  // Title fades in quickly at start
  const titleOpacity = easeOutCubic(Math.min(1, rawProgress / 0.15));

  // Compute per-pill state
  const pillState = (i: number) => {
    const delay = PILL_DELAYS[i];
    const window = 0.35; // each pill animates over 35% of progress
    const localT = Math.max(0, Math.min(1, (rawProgress - delay) / window));
    // Spring arrival with small overshoot — bar lands cleanly at final position
    const appear = springArrive(localT, 0.08);
    // Opacity tracks appearance but reaches full earlier
    const opacity = Math.min(1, appear * 1.6);
    // Scale: starts at 0.94, arrives at 1.0
    const scale = 0.94 + appear * 0.06;
    // Red glow: flashes as pill appears, fades shortly after
    const glow = hump(localT);
    return { opacity, scale, glow };
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '8px',
        border: `1px solid ${COLORS.border}`,
        padding: '20px 24px 24px',
        width: '100%',
        maxWidth: 'var(--chart-max-width, 1180px)',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '18px',
          opacity: titleOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            transform: `scale(${iconScale})`,
            transformOrigin: 'center',
            transition: 'none',
          }}
        >
          <WarningIcon color={COLORS.alertRed} />
        </div>
        <span
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: COLORS.titleText,
            letterSpacing: '-0.01em',
          }}
        >
          Compromised data
        </span>
      </div>

      {/* Grid of breach pills */}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '1.3fr 1fr 1fr',
          gridTemplateRows: 'repeat(2, minmax(52px, 1fr))',
          gap: '8px',
        }}
        onMouseMove={(e) => {
          if (isAnimating) return;
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }
        }}
      >
        {BREACHES.map((breach, i) => {
          const { opacity, scale, glow } = pillState(i);
          const glowBlur = 16 * glow;
          const glowSpread = 2 * glow;
          const glowAlpha = 0.35 * glow;
          const isHovered = hoveredIndex === i;
          // During animation, keep spring-driven scale; after animation, layer hover lift on top.
          const finalScale = isAnimating ? scale : (isHovered ? 1.015 : 1);
          return (
            <div
              key={breach.name}
              onMouseEnter={() => { if (!isAnimating) setHoveredIndex(i); }}
              onMouseLeave={() => { setHoveredIndex(null); setTooltipPos(null); }}
              style={{
                gridRow: breach.span ? 'span 2' : undefined,
                background: isHovered ? '#FDE3E0' : COLORS.pillFill,
                border: `1px solid ${isHovered ? '#F1C4C0' : COLORS.pillBorder}`,
                borderRadius: '10px',
                padding: breach.span ? '16px 24px' : '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                opacity,
                transform: `scale(${finalScale})`,
                transformOrigin: 'center',
                boxShadow:
                  glow > 0.01
                    ? `0 0 ${glowBlur}px ${glowSpread}px rgba(186, 20, 5, ${glowAlpha})`
                    : (isHovered ? '0 2px 8px rgba(186, 20, 5, 0.10)' : 'none'),
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: '14px',
                color: COLORS.pillText,
                letterSpacing: '-0.01em',
                minHeight: breach.span ? 'auto' : '44px',
                cursor: isAnimating ? 'default' : 'pointer',
                transition: isAnimating
                  ? 'none'
                  : 'background 180ms ease-out, border-color 180ms ease-out, transform 180ms cubic-bezier(0.2, 0.9, 0.3, 1.1), box-shadow 180ms ease-out',
              }}
            >
              <breach.Icon />
              <span style={{ fontWeight: 500 }}>{breach.name}</span>
            </div>
          );
        })}

        {/* Tooltip */}
        {!isAnimating && hoveredIndex !== null && tooltipPos && (() => {
          const breach = BREACHES[hoveredIndex];
          const containerW = containerRef.current?.clientWidth ?? 1000;
          const TOOLTIP_W = 260;
          const tipLeft = Math.min(
            Math.max(tooltipPos.x + 16, 0),
            containerW - TOOLTIP_W - 4
          );
          return (
            <div
              style={{
                position: 'absolute',
                left: tipLeft,
                top: tooltipPos.y - 80,
                width: TOOLTIP_W,
                background: '#FDFDFE',
                borderRadius: '4px',
                border: '1px solid #E4E7EB',
                padding: '10px 14px',
                boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.05)',
                fontFamily: "'Inter', -apple-system, sans-serif",
                color: '#3E4C59',
                pointerEvents: 'none',
                zIndex: 10,
                animation: 'breachTipEnter 140ms ease-out',
              }}
            >
              <div style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#3E4C59',
                letterSpacing: '-0.01em',
                marginBottom: 4,
              }}>
                {breach.tooltipTitle}
              </div>
              <div style={{
                fontSize: 13,
                fontWeight: 400,
                color: '#3E4C59',
                lineHeight: 1.45,
                letterSpacing: '-0.01em',
              }}>
                Your {breach.tooltipSubject} was exposed by{' '}
                <strong style={{ fontWeight: 700 }}>{breach.count}</strong>{' '}
                different breach{breach.count === 1 ? '' : 'es'}
              </div>
            </div>
          );
        })()}
      </div>
      <style>{`
        @keyframes breachTipEnter {
          from { opacity: 0; transform: translateY(-2px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
